/**
 * Cây tick chọn phạm vi chi nhánh.
 *
 * Chỉ vẽ và báo lựa chọn mới ra ngoài — không gọi API, không biết mình đang
 * nằm trong hộp thoại nào. Nhờ vậy màn Nhân sự (phạm vi Casdoor, có version +
 * xung đột 409) và màn Concept (lưu vào settings) dùng chung một cây, không
 * phải chép luật loại trừ nhóm/cơ sở ra hai nơi rồi lệch nhau.
 *
 * Luật tick nằm ở `toggleScopeCode`; ở đây chỉ nối sự kiện.
 */
import { useMemo } from "react";
import { Checkbox } from "@/ui/checkbox";
import {
  ALL_SCOPE,
  type ScopeUnit,
  branchScopeCode,
  buildScopeTreeIndex,
  scopeTreeRows,
  toggleScopeCode,
} from "./scopeCodes";

type Props = {
  units: readonly ScopeUnit[];
  selected: readonly string[];
  /** Nhận danh sách mã ĐÃ chuẩn hoá, không phải sự kiện tick thô. */
  onChange: (next: string[]) => void;
  allLabel?: string;
  /** Số pixel thụt vào mỗi cấp. Màn hẹp nên để nhỏ hơn. */
  indentStep?: number;
  /** Chiều cao tối thiểu mỗi dòng — vùng chạm trên điện thoại. */
  rowMinHeight?: string;
  /**
   * Trần chiều cao vùng cuộn của cây.
   *
   * Mặc định 52vh hợp với hộp thoại mà cây LÀ nội dung chính (Phạm vi chi
   * nhánh bên Nhân sự). Khi cây chỉ là một ô giữa form dài, truyền giá trị
   * cố định nhỏ hơn — nửa màn hình nằm trong một hộp thoại vốn đã cuộn sẽ
   * thành hai vùng cuộn lồng nhau, kéo chuột không biết vùng nào ăn.
   */
  maxHeight?: string;
  disabled?: boolean;
};

export function BranchScopeTree({
  units,
  selected,
  onChange,
  allLabel = "Tất cả chi nhánh",
  indentStep = 20,
  rowMinHeight = "min-h-8",
  maxHeight = "max-h-[52vh]",
  disabled = false,
}: Props) {
  const tree = useMemo(() => buildScopeTreeIndex(units), [units]);
  const rows = useMemo(() => scopeTreeRows(units), [units]);

  const toggle = (code: string, checked: boolean) => {
    onChange(toggleScopeCode(selected, code, checked, tree));
  };

  return (
    <div className={`${maxHeight} space-y-1 overflow-y-auto rounded-md border p-2`}>
      <label
        className={`flex ${rowMinHeight} items-center gap-2 px-2 text-sm font-medium`}
      >
        <Checkbox
          checked={selected.includes(ALL_SCOPE)}
          disabled={disabled}
          onCheckedChange={(checked) => toggle(ALL_SCOPE, checked === true)}
        />
        {allLabel}
      </label>
      {rows.map(({ unit, depth }) => {
        const code = branchScopeCode(unit);
        // Cơ sở đã ngưng vẫn hiện để GỠ được khỏi phạm vi cũ, chỉ chặn thêm mới.
        const inactive = unit.isActive === false && !selected.includes(code);
        return (
          <label
            key={unit.code}
            className={`flex ${rowMinHeight} items-center gap-2 rounded px-2 text-sm hover:bg-muted/40`}
            style={{ paddingLeft: `${8 + depth * indentStep}px` }}
          >
            <Checkbox
              checked={selected.includes(code)}
              disabled={disabled || inactive}
              onCheckedChange={(checked) => toggle(code, checked === true)}
              aria-label={unit.name}
            />
            <span className={inactive ? "text-muted-foreground line-through" : ""}>
              {unit.name}
            </span>
            <span className="text-xs text-muted-foreground">{unit.code}</span>
            {inactive && (
              <span className="text-xs text-muted-foreground">Ngưng hoạt động</span>
            )}
          </label>
        );
      })}
    </div>
  );
}
